import { Injectable, Injector, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, Subscription, tap, throwError } from 'rxjs';
import { ConfigProvider } from "../../config-provider.service";
import { UtilsProvider } from "./utils-provider.service";
import { catchError, map } from "rxjs/operators";
import { ApiError } from "../error/api-error";
import { LangsProvider } from "./langs-provider";
import { Lang } from "../models/lang";
import { AthletesProvider } from "../../athletes/providers/athletes-provider";

@Injectable(
    {
        providedIn: 'root'
    }
)
export class ApiBaseService implements OnDestroy {
  private subscriptions: Subscription[] = [];

  private _langsProvider: LangsProvider | null = null;
  private get langsProvider(): LangsProvider {
    if (!this._langsProvider) {
      this._langsProvider = this.injector.get(LangsProvider);
    }
    return this._langsProvider;
  }

  constructor(
    private _http: HttpClient,
    private configProvider: ConfigProvider,
    private injector: Injector,
  ) {
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Base method to call a webservice
   * @param method            HTTP method get|post|put|delete
   * @param url               the full url of the webservice
   * @param params            key-value object containing the parameters to send with the webservice call
   * @param extra             an literal object containing extra configuration of the webservice call ; 'accessToken' add a header container the bearer authorization token.
   * @param asApplicationJson Use calls as application/json instead of multipart/form-data
   */
  public call(method: string, url: string, params: Object = {}, extra: any = null, asApplicationJson = true, range = false): Observable<any> {
    let headers: HttpHeaders = new HttpHeaders({
      'Accept-Language': this.langsProvider.getBestLangCode(),
    });

    if (range) {
      headers = headers.set('Range', 'bytes=0-1023');
    }
    const isFormData = params instanceof FormData;

    if (!isFormData && asApplicationJson) {
      headers = headers
        .set('Content-Type', 'application/json')
        .set('Accept', 'application/json');
    }

    if (typeof extra === 'string') {
      extra = {accessToken: extra};
    } else if (!extra) {
      extra = {resultType: 'json'}; // Default resultType to 'json' if not specified
    }

    if (extra.accessToken) {
      headers = headers.set('Authorization', 'Bearer ' + extra.accessToken);
    }

    const callParams: any = {headers: headers};

    if (extra.resultType === 'file') {
      callParams.responseType = 'blob';
    }

    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = this.configProvider.apiRootUrl + url;
    }

    let call$: Observable<any>;

    if (method.toLowerCase() === 'get') {
      call$ = this._http.get(url + this.encodeParams(params), {...callParams, transferCache: false});
    } else {
      // Utiliser FormData directement si c'est le cas
      const body = isFormData ? params : asApplicationJson ? JSON.stringify(params) : this.buildFormData(params);
      if (method.toLowerCase() === 'post') {
        call$ = this._http.post(url, body, callParams);
      } else if (method.toLowerCase() === 'put') {
        call$ = this._http.put(url, body, callParams);
      } else if (method.toLowerCase() === 'patch') {
        call$ = this._http.patch(url, body, callParams);
      } else if (method.toLowerCase() === 'delete') {
        call$ = this._http.delete(url + this.encodeParams(params), callParams);
      } else {
        // Handle unrecognized method
        throw new Error('API call method unrecognized: ' + method);
      }
    }
    return call$;
  }

  private encodeParams(params: any): string {
    const paramsElems = [];
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        paramsElems.push(encodeURIComponent(key) + '=' + encodeURIComponent(params[key]));
      }
    }
    let paramsEncoded = '';
    if (paramsElems.length) {
      paramsEncoded += '?' + paramsElems.join('&');
    }

    return paramsEncoded;
  }

  private buildFormData(params: any): FormData {
    const getData = (keys: string[], data: any): any[] => {
      let items: any[] = [];
      const currentKey: string = keys[keys.length - 1];

      if (data[currentKey] instanceof File) {
        const firstKey: string = keys.splice(0, 1)[0];
        let keysStr: string = firstKey;
        if (keys.length) {
          keysStr += '[' + keys.join('][') + ']';
        }
        items.push({key: keysStr, value: data[currentKey], fileName: data[currentKey].name});
      } else if (data[currentKey] instanceof Array) {
        for (let i = 0; i < data[currentKey].length; i++) {
          items = items.concat(getData(keys.concat(['' + i]), data[currentKey]));
        }
      } else if (data[currentKey] instanceof Object) {
        for (const itemKey in data[currentKey]) {
          if (data[currentKey].hasOwnProperty(itemKey)) {
            items = items.concat(getData(keys.concat([itemKey]), data[currentKey]));
          }
        }
      } else {
        let dataValue: any = data[currentKey];
        if (dataValue === undefined || dataValue === null) {
          // c'est un hack très vilain
          // mais formdata.append(null) revient à faire formdata.append('null')
          // ce qui est vraiment pas ce qu'on veut ...
          dataValue = '';
        }
        if (dataValue !== null) {
          const firstKey: string = keys.splice(0, 1)[0];
          let keysStr: string = firstKey;
          if (keys.length) {
            keysStr += '[' + keys.join('][') + ']';
          }
          items.push({key: keysStr, value: dataValue});
        }
      }

      return items;
    };

    const formData: FormData = new FormData();
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        const data: any = getData([key], params);
        for (let i = 0; i < data.length; i++) {
          if (data[i].fileName) {
            formData.append(data[i].key, data[i].value, data[i].fileName);
          } else {
            formData.append(data[i].key, data[i].value);
          }
        }
      }
    }

    return formData;
  }


  public specialCall(path: string, params: Object = {}): Observable<any> {
    return this._http.get(`${this.configProvider.apiRootUrl}${path}`, params)
      .pipe(
        catchError((err) => {
          throw (this.formatErrors(err));
        })
      );
  }

  private formatErrors(error: any) {
    console.log(error);
    if (error.error.type && 'app.exception' === error.error.type) {
      return new ApiError(error.status, error.error.type, error.error.message);
    }

    return new Error(error.error);
  }

}
